/* eslint-disable */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import GenericCard from '@ui-components/Card/GenericCard';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import GenericTabs, { TabOption } from '@ui-components/Tabs/GenericTabs';
import { cloudElementsColumns, ctxrInterfacesColumns, getReflectorDetailsSections } from './ReflectorDetailsData';
import { transformReflectorDetails } from '../../../helpers/formattersShapedData';
import CopyableValue from '@shared/components/CopyableValue';
import { ReactComponent as IconEdit } from '../../../../shared/assets/Icons/IconEdit_KE.svg';
import { ReactComponent as IconCheck } from '../../../../shared/assets/Icons/IconCheckSquared.svg';
import { ReactComponent as IconCancel } from '../../../../shared/assets/Icons/IconCrossSquare.svg';
import GenericButton from '@ui-components/Button/GenericButton';
import { ReactComponent as IconRefresh } from '../../../../shared/assets/Icons/IconRefreshV2.svg';
import { GenericTable } from '@ui-components/Table/GenericTable';
import GenericSearchbar, { ParsedFilter } from '@ui-components/Searchbar/GenericSearchbar';
import IAMPermissionsModal from './IAMPermisions/IAMPermissionsModal';
import { Reflector } from '../ReflectorData';
import { useAuth0 } from "@auth0/auth0-react";
import { getReflectorDetails, getReflectorBgpConfig, getCloudPropagationRules, getCloudPropagationRulesByCluster, getReflectorIamPermissions, updateReflectorName, updateReflectorTags } from '../request/request';
import BgpDetailsPage from '../BgpDetails/BgpDetailsPage';
import CloudPropagationPage from '../CloudPropagation/CloudPropagationPage';
import { IAMPermission } from './IAMPermisions/IAMPermisionsModalData';
import { ReactComponent as IconWarning } from '../../../../shared/assets/Icons/Icon_Warning.svg';
import { cloudPropagationColumns } from '../CloudPropagation/CloudPropagationData';
import ClusterModal from '../Cluster/ClusterModal/ClusterModal';
import { createCluster } from '../Cluster/request/request';
import axios from 'axios';
import { AxiosError } from 'axios';
import EmptyItems from '@shared/components/EmptyItems';
import { filterCloudElements, filterInterfaces } from '@shared/components/utils/searchUtils';

// Using centralized mapAvailabilityToStatus from formattersShapedData

const TABS: TabOption[] = [
  { label: 'Details', value: 'Details' },
  { label: 'BGP', value: 'BGP' },
  { label: 'Cloud Propagation', value: 'Cloud Propagation' },
];

type LocationState = { selectedReflectorRow?: Reflector };

const ClusterConfigAlert = ({ clusterName, clusterId }: { clusterName: string; clusterId?: string }) => {
  const navigate = useNavigate();
  
  const handleClusterClick = () => {
    if (clusterId) {
      navigate(`/reflector-v2/management/clusters/details/${clusterId}`);
    }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded p-4 flex items-start gap-2 my-4">
      <IconWarning className="w-5 h-5 text-blue-700 mt-1" />
      <div>
        <div className="font-semibold text-blue-700">Cluster Configuration Required</div>
        <div className="text-blue-700 text-sm">
          This reflector is part of cluster{' '}
          {clusterId ? (
            <span 
              className="underline font-medium cursor-pointer hover:text-blue-800"
              onClick={handleClusterClick}
            >
              {clusterName}
            </span>
          ) : (
            <span className="underline font-medium">{clusterName}</span>
          )}
          . Configuration must be done at the cluster level.
        </div>
      </div>
    </div>
  );
};

function getIamTagState(permissions: IAMPermission[]) {
  if (permissions.length === 0) return { label: '-', variant: 'neutral' };
  const allAllowed = permissions.every(p => p.enabled);
  const allDenied = permissions.every(p => !p.enabled);
  if (allAllowed) return { label: 'Allowed', variant: 'success' };
  if (allDenied) return { label: 'Denied', variant: 'error' };
  return { label: 'Degraded', variant: 'warning' };
}

// Helper to determine if cluster config alert should be shown
function isClusterConfig(reflector?: Reflector) {
  if (!reflector) return false;
  
  // Check if reflector has a cluster name (not empty, not dash, not undefined)
  const hasClusterName = reflector.cluster && 
    reflector.cluster !== '—' && 
    reflector.cluster !== '-' && 
    reflector.cluster !== '' && 
    reflector.cluster !== 'undefined';
  
  // Check if reflector has a clusterId
  const hasClusterId = reflector.clusterId && 
    reflector.clusterId !== '—' && 
    reflector.clusterId !== '-' && 
    reflector.clusterId !== '' && 
    reflector.clusterId !== 'undefined';
  
  return hasClusterName || hasClusterId;
}

// Helper to check if we're on a cluster route
function isClusterRoute() {
  return window.location.pathname.includes('/clusters/details/');
}

function ReflectorDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { getAccessTokenSilently } = useAuth0();
  const [activeTab, setActiveTab] = useState<string>('Details');
  const { selectedReflectorRow: row } = (location.state as LocationState) ?? {};
  const [reflector, setReflector] = useState<Reflector | undefined>(row);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [isClusterModalOpen, setIsClusterModalOpen] = useState(false);

  const [bgpConfig, setBgpConfig] = useState(null);
  const [bgpLoading, setBgpLoading] = useState(false);
  const [bgpError, setBgpError] = useState<string | null>(null);

  const [cloudPropagationRules, setCloudPropagationRules] = useState([]);
  const [cloudPropagationLoading, setCloudPropagationLoading] = useState(false);
  const [cloudPropagationError, setCloudPropagationError] = useState<string | null>(null);
  const [clusterIdFromResponse, setClusterIdFromResponse] = useState<number | undefined>(undefined);

  const [isIamModalOpen, setIsIamModalOpen] = useState(false);
  const [iamPermissions, setIamPermissions] = useState<IAMPermission[]>([]);
  const [iamPermissionsLoading, setIamPermissionsLoading] = useState(false);
  const [iamPermissionsError, setIamPermissionsError] = useState<string | null>(null);

  // Editable state for Name and Tag
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingTag, setIsEditingTag] = useState(false);
  const [nameValue, setNameValue] = useState(row?.name || '');
  const [tagValue, setTagValue] = useState<string>(typeof row?.tags === 'string' ? row.tags : '');
  const [originalTag, setOriginalTag] = useState<string>('');

  // Build detailsSections dynamically from reflector data
  const detailsSections = getReflectorDetailsSections(reflector);

  const fetchIamPermissions = async () => {
    if (!reflector?.id) return;
    
    setIamPermissionsLoading(true);
    setIamPermissionsError(null);
    try {
      const token = await getAccessTokenSilently();
      const response = await getReflectorIamPermissions(token, Number(reflector.id));
      setIamPermissions(response.data);
    } catch (err) {
      setIamPermissionsError('Failed to load IAM permissions');
      console.error('Error fetching IAM permissions:', err);
    } finally {
      setIamPermissionsLoading(false);
    }
  };

  useEffect(() => {
    if (reflector?.id) {
      fetchIamPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reflector?.id, getAccessTokenSilently]);

  useEffect(() => {
    const fetchDetails = async () => {
      if (id && (!reflector || !reflector.data || !reflector.cloudElements || !reflector.interfaces)) {
        setDetailsLoading(true);
        try {
          const token = await getAccessTokenSilently();
          const response = await getReflectorDetails(token, Number(id));
          // Use centralized transformer
          const transformedReflector = transformReflectorDetails(response.data);
          setTagValue(transformedReflector.tags);
          setOriginalTag(transformedReflector.tags);
          setReflector(transformedReflector);
        } catch (error) {
          console.error('Error fetching reflector details:', error);
        } finally {
          setDetailsLoading(false);
        }
      }
    };
    fetchDetails();
  }, [id, reflector, getAccessTokenSilently]);

  // Fetch BGP data when BGP tab is activated
  useEffect(() => {
    const fetchBgpData = async () => {
      // For cluster routes, always fetch data
      // For reflector routes, only fetch if not a cluster config
      const shouldFetch = isClusterRoute() || 
        (activeTab === 'BGP' && reflector?.id && !isClusterConfig(reflector));
      
      if (shouldFetch && activeTab === 'BGP' && reflector?.id) {
        setBgpLoading(true);
        setBgpError(null);
        try {
          const token = await getAccessTokenSilently();
          const configRes = await getReflectorBgpConfig(token, Number(reflector.id));
          setBgpConfig(configRes.data);
        } catch (err) {
          // Check if it's a 404 error (no initial config) - don't set error in this case
          if (err instanceof AxiosError && err.response?.status === 404) {
            console.log('No initial BGP config found, user can create one');
            setBgpConfig(null); // Set to null to indicate no config exists
          } else {
            setBgpError('Failed to load BGP configuration');
          }
        } finally {
          setBgpLoading(false);
        }
      }
    };
    fetchBgpData();
  }, [activeTab, reflector?.id, getAccessTokenSilently]);

  // Fetch Cloud Propagation data when Cloud Propagation tab is activated
  useEffect(() => {
    const fetchCloudPropagation = async () => {
      // For cluster routes, always fetch data
      // For reflector routes, only fetch if not a cluster config
      const shouldFetch = isClusterRoute() || 
        (activeTab === 'Cloud Propagation' && reflector?.id && !isClusterConfig(reflector));
      
      if (shouldFetch && activeTab === 'Cloud Propagation' && reflector?.id) {
        setCloudPropagationLoading(true);
        setCloudPropagationError(null);
        try {
          const token = await getAccessTokenSilently();
          
          // If reflector is part of a cluster, fetch cluster propagation rules
          if (isClusterConfig(reflector) && reflector.clusterId) {
            const rulesRes = await getCloudPropagationRulesByCluster(token, Number(reflector.clusterId));
            setCloudPropagationRules(rulesRes.data);
            setClusterIdFromResponse(Number(rulesRes.data.clusterId));
          } else {
            // Fetch individual reflector propagation rules
            const rulesRes = await getCloudPropagationRules(token, Number(reflector.id));
            setCloudPropagationRules(rulesRes.data);
            setClusterIdFromResponse(undefined); // Clear clusterId if not a cluster config
          }
        } catch (err) {
          setCloudPropagationError('Failed to load Cloud Propagation rules');
        } finally {
          setCloudPropagationLoading(false);
        }
      }
    };
    fetchCloudPropagation();
  }, [activeTab, reflector?.id, reflector?.clusterId, getAccessTokenSilently]);

  // Save handlers
  const handleSaveName = async () => {
    if (!reflector?.id) return;
    
    try {
      const token = await getAccessTokenSilently();
      await updateReflectorName(token, Number(reflector.id), { name: nameValue });
      setIsEditingName(false);
      // Refresh reflector details to get updated data
      const response = await getReflectorDetails(token, Number(reflector.id));
      setReflector(prev => ({
        ...prev,
        ...response.data,
        name: nameValue,
      }));
    } catch (error) {
      console.error('Error updating reflector name:', error);
      // Revert to original name on error
      setNameValue(row?.name || '');
    }
  };

  const handleSaveTag = async () => {
    if (!reflector?.id) return;
    
    try {
      const token = await getAccessTokenSilently();
      await updateReflectorTags(token, Number(reflector.id), { tags: tagValue.trim() });
      setOriginalTag(tagValue);
      setIsEditingTag(false);
      // Refresh reflector details to get updated data
      const response = await getReflectorDetails(token, Number(reflector.id));
      setReflector(prev => ({
        ...prev,
        ...response.data,
      }));
    } catch (error) {
      console.error('Error updating reflector tags:', error);
      // Revert to original tag on error
      setTagValue(originalTag);
    }
  };

  const handleCancelEditName = () => {
    setNameValue(row?.name || '');
    setIsEditingName(false);
  };

  const handleCancelEditTag = () => {
    setTagValue(originalTag);
    setIsEditingTag(false);
  };

  // Search filters for Cloud Elements Discovered
  const [cloudElementsFilters, setCloudElementsFilters] = useState<ParsedFilter[]>([]);
  const cloudElementsSuggestedKeys = ['Type', 'Element ID', 'VPC', 'Region', 'Tags', 'Discovered At'];

  // Search filters for CTXR Interfaces
  const [ctxrInterfacesFilters, setCtxrInterfacesFilters] = useState<ParsedFilter[]>([]);
  const ctxrInterfacesSuggestedKeys = ['Interface', 'IP Address', 'MAC Address', 'VPC ID', 'Cloud Subnet ID'];

  // Apply filters to data using utility functions
  const filteredCloudElements = filterCloudElements(reflector?.cloudElements || [], cloudElementsFilters);
  const filteredInterfaces = filterInterfaces(reflector?.interfaces || [], ctxrInterfacesFilters);

  const navigate = useNavigate();

  // Loader overlay state
  const isOverlayLoading =
    (detailsLoading && activeTab === 'Details') ||
    (activeTab === 'BGP' && bgpLoading) ||
    (activeTab === 'Cloud Propagation' && cloudPropagationLoading);

  // Refresh function for details and IAM permissions
  const handleRefreshDetails = async () => {
    if (!reflector?.id) return;
    
    setDetailsLoading(true);
    try {
      // Refresh reflector details
      const token = await getAccessTokenSilently();
      const response = await getReflectorDetails(token, Number(reflector.id));
      // Use centralized transformer
      const transformedReflector = transformReflectorDetails(response.data);
      setTagValue(transformedReflector.tags);
      setOriginalTag(transformedReflector.tags);
      setReflector(transformedReflector);

      // Refresh IAM permissions
      await fetchIamPermissions();
    } catch (error) {
      console.error('Error refreshing details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleClusterSubmit = async (data: any) => {
    try {
      const token = await getAccessTokenSilently();
      
      // Validate required data
      if (!data.selectedReflectors || !Array.isArray(data.selectedReflectors) || data.selectedReflectors.length === 0) {
        throw new Error('No reflectors selected for cluster creation');
      }

      if (!data.description) {
        throw new Error('Cluster description is required');
      }

      
      // Get the cloud and region from the current reflector if available
      const currentReflectorCloud = reflector?.cloud || '-';
      const currentReflectorRegion = reflector?.region || '-';
      
      // Format the payload according to the API requirements
      const payload = {
        description: data.description,
        region: currentReflectorRegion,
        cloud: currentReflectorCloud,
        status: 'active',
        reflectors: data.selectedReflectors.map((reflector: any, index: number) => {
          if (!reflector || !reflector.id) {
            throw new Error(`Invalid reflector data at index ${index}`);
          }
          return {
            id: Number(reflector.id),
            role: data.roles[reflector.id]?.role || (index === 0 ? 'primary' : 'secondary')
          };
        })
      };

      
      // Create the cluster
       await createCluster(token, payload);

      // Refresh reflector details to show updated cluster info
      const detailsResponse = await getReflectorDetails(token, Number(reflector?.id));
      setReflector(prev => ({
        ...prev,
        ...detailsResponse.data,
        cluster: data.clusterName,
      }));

      // Close the modal
      setIsClusterModalOpen(false);
    } catch (error) {
      console.error('Error creating cluster:', error);
      if (axios.isAxiosError(error)) {
        console.error('API Error details:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        });
      }
    }
  };

  return (
    <div className="relative">
      {isOverlayLoading && (
        <div
          className="fixed inset-0 flex flex-col items-center justify-center bg-black/50 z-[9999]"
        >
          <h2 className="text-xl font-semibold mb-4 text-white">
            Processing request...
          </h2>
          <div
            className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500"
          />
        </div>
      )}
      <div className="mb-4 flex items-center gap-2">
        <GenericButton
          label="Back"
          variant="secondary"
          shape="ghost"
          size="sm"
          iconLeftName="arrowLeft"
          onClick={() => navigate('/reflector-v2/management/bgp-reflectors')}
        />
      </div>
      <div className="flex items-center justify-start mb-2 max-w-[600px]">
        <GenericTabs
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>
      {/* Main content below loader */}
      {activeTab === 'Details' && (
        <div className="space-y-4">
          {/* Top row: Reflector Details and Interfaces */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Reflector Details Card */}
            <GenericCard
              isCollapsible={false}
              headerContent={
                <div className="flex items-center justify-between w-full">
                  <span>Details</span>
                  <GenericButton
                    icon={<IconRefresh className="w-4 h-4" />}
                    label="Sync"
                    variant="refresh"
                    shape="ghost"
                    size="sm"
                    aria-label="Refresh Details"
                    onClick={handleRefreshDetails}
                  />
                </div>
              }
            >
              <div className="divide-y divide-gray-100">
                {detailsSections[0]?.items.map((item, i) => {
                  // Editable logic for Name and Tag
                  if (item.label === 'Name') {
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center space-x-2 w-1/2 px-4 justify-end">
                          {isEditingName ? (
                            <>
                              <GenericButton
                                onClick={handleSaveName}
                                icon={<IconCheck className="w-5 h-5 text-semantic-text-secondary" />}
                                isIconOnly
                                variant="secondary"
                                shape="ghost"
                                size="sm"
                                aria-label="Save Name"
                              />
                              <GenericButton
                                onClick={handleCancelEditName}
                                icon={<IconCancel className="w-5 h-5 text-semantic-text-secondary" />}
                                isIconOnly
                                variant="secondary"
                                shape="ghost"
                                size="sm"
                                aria-label="Cancel Edit Name"
                              />
                              <input
                                type="text"
                                value={nameValue}
                                onChange={e => setNameValue(e.target.value)}
                                className="border border-gray-300 rounded px-2 py-1 text-sm w-40"
                              />
                            </>
                          ) : (
                            <>
                              <GenericButton
                                onClick={() => setIsEditingName(true)}
                                icon={<IconEdit className="w-4 h-4" />}
                                isIconOnly
                                variant="secondary"
                                shape="ghost"
                                size="sm"
                                aria-label="Edit Name"
                              />
                              <span className="text-sm text-semantic-text-secondary">{nameValue}</span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  }
                  if (item.label === 'Tag') {
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center space-x-2 w-1/2 px-4 justify-end">
                          {isEditingTag ? (
                            <>
                              <GenericButton
                                onClick={handleSaveTag}
                                icon={<IconCheck className="w-5 h-5 text-semantic-text-secondary" />}
                                isIconOnly
                                variant="secondary"
                                shape="ghost"
                                size="sm"
                                aria-label="Save Tag"
                              />
                              <GenericButton
                                onClick={handleCancelEditTag}
                                icon={<IconCancel className="w-5 h-5 text-semantic-text-secondary" />}
                                isIconOnly
                                variant="secondary"
                                shape="ghost"
                                size="sm"
                                aria-label="Cancel Edit Tag"
                              />
                              <input
                                type="text"
                                value={tagValue}
                                onChange={e => setTagValue(e.target.value)}
                                placeholder="Enter tags separated by commas"
                                className="border border-gray-300 rounded px-2 py-1 text-sm w-40"
                              />
                            </>
                          ) : (
                            <>
                              <GenericButton
                                onClick={() => setIsEditingTag(true)}
                                icon={<IconEdit className="w-4 h-4" />}
                                isIconOnly
                                variant="secondary"
                                shape="ghost"
                                size="sm"
                                aria-label="Edit Tag"
                              />
                              <GenericTag label={tagValue || '-'} variant="neutral" />
                            </>
                          )}
                        </div>
                      </div>
                    );
                  }
                  // Cluster as tag
                  if (item.label === 'Cluster') {
                    const clusterVal = String(item.value).trim();
                    const isUnset = !clusterVal || clusterVal === '-' || clusterVal === '—';
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center w-1/2 px-4 justify-end">
                          {isUnset ? (
                            <GenericButton 
                              label="Add Cluster" 
                              size="sm" 
                              variant="secondary" 
                              shape="ghost" 
                              onClick={() => setIsClusterModalOpen(true)} 
                            />
                          ) : (
                            <span 
                              className="cursor-pointer hover:opacity-80"
                              onClick={() => {
                                if (reflector?.clusterId) {
                                  navigate(`/reflector-v2/management/clusters/details/${reflector.clusterId}`);
                                }
                              }}
                            >
                              <GenericTag label={clusterVal} variant="neutral" />
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  }
                  // Cloud as tag
                  if (item.label === 'Cloud') {
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center w-1/2 px-4 justify-end">
                          <GenericTag label={String(item.value)} variant="warning" />
                        </div>
                      </div>
                    );
                  }
                  // Region as tag
                  if (item.label === 'Region') {
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center w-1/2 px-4 justify-end">
                          <GenericTag label={String(item.value)} variant="neutral" />
                        </div>
                      </div>
                    );
                  }
                  // Status as tag
                  if (item.label === 'Status') {
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center w-1/2 px-4 justify-end">
                          <GenericTag label={String(item.value)} variant={item.variant as any} />
                        </div>
                      </div>
                    );
                  }
                  // IAM Permissions as clickable tag
                  if (item.label === 'IAM Permissions') {
                    const { label: iamTagLabel, variant: iamTagVariant } = getIamTagState(iamPermissions);
                    return (
                      <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                        <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                        <div className="flex items-center w-1/2 px-4 justify-end space-x-2">
                          <span
                            className="cursor-pointer hover:opacity-80"
                            onClick={() => setIsIamModalOpen(true)}
                          >
                            <GenericTag label={iamTagLabel} variant={iamTagVariant as any} />
                          </span>
                          {/* <GenericButton
                            icon={<IconRefresh className="w-4 h-4" />}
                            label="Sync"
                            variant="refresh"
                            shape="ghost"
                            size="sm"
                            aria-label="Refresh IAM Permissions"
                            onClick={fetchIamPermissions}
                          /> */}
                          <IAMPermissionsModal 
                            isOpen={isIamModalOpen} 
                            onClose={() => setIsIamModalOpen(false)} 
                            permissions={iamPermissions}
                            loading={iamPermissionsLoading}
                            error={iamPermissionsError}
                          />
                        </div>
                      </div>
                    );
                  }
                  // Default rendering (plain text)
                  return (
                    <div key={i} className="flex justify-between w-full py-1 items-center min-h-[40px]">
                      <h3 className="text-sm text-semantic-text-primary w-1/2 px-4">{item.label}</h3>
                      <div className="flex items-center w-1/2 px-4 justify-end">
                        {item.value ? (
                          // Use CopyableValue for ID fields
                          (item.label === 'ID' || item.label === 'UUID' || item.label === 'License ID' || item.label === 'VPC/VNET/VCN') ? (
                            <CopyableValue value={String(item.value)} />
                          ) : (
                            <span className="text-sm text-semantic-text-secondary">{item.value}</span>
                          )
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </GenericCard>

            {/* Interfaces Card */}
            <GenericCard
              isCollapsible={false}
              headerContent="Interfaces"
            >
              <div className="py-2 px-4">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="flex-1">
                    <GenericSearchbar
                      placeholder="Search CTXR Interfaces..."
                      suggestedKeys={ctxrInterfacesSuggestedKeys}
                      onFiltersChange={setCtxrInterfacesFilters}
                    />
                  </div>
                </div>
                <GenericTable
                  columns={ctxrInterfacesColumns}
                  data={filteredInterfaces}
                  initialRowsPerPage={5}
                  rowsPerPageOptions={[5, 10]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                />
              </div>
            </GenericCard>
          </div>

          {/* Bottom row: Cloud Elements Discovered - Full Width */}
          <GenericCard
            isCollapsible={false}
            headerContent="Cloud Elements Discovered"
          >
            <div className="py-2 px-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex-1">
                  <GenericSearchbar
                    placeholder="Search Cloud Elements..."
                    suggestedKeys={cloudElementsSuggestedKeys}
                    onFiltersChange={setCloudElementsFilters}
                  />
                </div>
              </div>
              <GenericTable
                columns={cloudElementsColumns}
                data={filteredCloudElements}
                initialRowsPerPage={5}
                rowsPerPageOptions={[5, 10]}
                width="100%"
                tableClassName="bg-semantic-bg-primary"
              />
            </div>
          </GenericCard>
        </div>
      )}
      {activeTab === 'BGP' && (
        // On cluster routes, always show the BGP content
        // On reflector routes, show alert if it's a cluster config
        isClusterRoute() ? (
          <>
            {bgpLoading && (
              <div className="fixed inset-0 flex flex-col items-center justify-center bg-black/50 z-[9999]">
                <h2 className="text-xl font-semibold mb-4 text-white">
                  Processing request...
                </h2>
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500" />
              </div>
            )}
            {bgpError ? (
              <EmptyItems />
            ) : (
              <BgpDetailsPage
                config={bgpConfig}
                reflectorId={reflector?.id}
                getAccessTokenSilently={getAccessTokenSilently}
                context="reflector"
              />
            )}
          </>
        ) : (
          reflector && isClusterConfig(reflector)
            ? <ClusterConfigAlert clusterName={reflector.cluster} clusterId={reflector.clusterId} />
            : (
              <>
                {bgpLoading && (
                  <div className="fixed inset-0 flex flex-col items-center justify-center bg-black/50 z-[9999]">
                    <h2 className="text-xl font-semibold mb-4 text-white">
                      Processing request...
                    </h2>
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500" />
                  </div>
                )}
                {bgpError ? (
                  <EmptyItems />
                ) : (
                  <BgpDetailsPage
                    config={bgpConfig}
                    reflectorId={reflector?.id}
                    getAccessTokenSilently={getAccessTokenSilently}
                    context="reflector"
                  />
                )}
              </>
            )
        )
      )}
      {activeTab === 'Cloud Propagation' && (
        // On cluster routes, always show the table
        // On reflector routes, show alert if it's a cluster config
        isClusterRoute() ? (
          <CloudPropagationPage
            data={cloudPropagationRules}
            columns={cloudPropagationColumns}
            error={cloudPropagationError}
            getAccessTokenSilently={getAccessTokenSilently}
            clusterId={clusterIdFromResponse}
          />
        ) : (
          reflector && isClusterConfig(reflector)
            ? <ClusterConfigAlert clusterName={reflector.cluster} clusterId={reflector.clusterId} />
            : (
              <CloudPropagationPage
                data={cloudPropagationRules}
                columns={cloudPropagationColumns}
                error={cloudPropagationError}
                getAccessTokenSilently={getAccessTokenSilently}
                clusterId={clusterIdFromResponse}
              />
            )
        )
      )}

      <ClusterModal
        isOpen={isClusterModalOpen}
        onClose={() => setIsClusterModalOpen(false)}
        onSubmit={handleClusterSubmit}
      />
    </div>
  );
}

export default ReflectorDetailsPage;